import { useState, useEffect } from 'react';
import { formatPhone } from '../components/formatPhone';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { deleteUser, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import { FaUser, FaEnvelope, FaPhone, FaBuilding, FaLock, FaTrash } from 'react-icons/fa';
import './Sidebar.css';
import './Navbar.css';
import './Settings.css';
import LoadingScreen from './LoadingScreen';
import { showToast } from '../components/toast';

const BASE_URL = 'https://api.rside.app';

function Settings() {
  const navigate = useNavigate();
  const [initials, setInitials] = useState('');
  const [profile, setProfile] = useState({ Full_Name: '', Email: '', Phone_Number: '', Company: '' });
  const [loading, setLoading] = useState(true);

  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');

  // Edit profile state
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editForm, setEditForm] = useState({ Full_Name: '', Phone_Number: '', Company: '' });
  const [editMsg, setEditMsg] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteMsg, setDeleteMsg] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      const user = auth.currentUser;
      if (!user) return navigate('/login');

      try {
        const res = await fetch(
          `${BASE_URL}/MyAccount?Email=${encodeURIComponent(user.email)}`
        );

        const data = await res.json();

        const parts = data.Full_Name?.trim().split(/\s+/) || [];
        setInitials(((parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '')).toUpperCase());

        setProfile({
          Full_Name: data.Full_Name || '',
          Email: data.Email || user.email,
          Phone_Number: data.Phone_Number || '',
          Company: data.Company || ''
        });
      } catch (err) {
        console.error('Error loading profile:', err);
      } finally {
        setLoading(false);
      }
    };

    const unsub = auth.onAuthStateChanged((u) => u ? fetchProfile() : navigate('/login'));
    return unsub;
  }, [navigate]);

  const handleEditOpen = () => {
    setEditForm({ Full_Name: profile.Full_Name, Phone_Number: profile.Phone_Number, Company: profile.Company });
    setEditMsg('');
    setShowEditProfile(true);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setEditMsg('');
    setEditSaving(true);
    try {
      const params = new URLSearchParams({
        Full_Name: editForm.Full_Name,
        Phone_Number: editForm.Phone_Number,
        Company: editForm.Company,
        Email: profile.Email,
      });
      const res = await fetch(`${BASE_URL}/UpdatePropertyManager?${params}`, { method: 'PUT' });
      if (!res.ok) throw new Error();
      setProfile(prev => ({ ...prev, Full_Name: editForm.Full_Name, Phone_Number: editForm.Phone_Number, Company: editForm.Company }));
      const parts = editForm.Full_Name.trim().split(/\s+/);
      setInitials(((parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '')).toUpperCase());
      setShowEditProfile(false);
    } catch {
      setEditMsg('Failed to save changes. Please try again.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMsg('');

    if (newPassword !== confirmPassword) {
      setPasswordMsg('Passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordMsg('Password must be at least 6 characters.');
      return;
    }

    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setPasswordMsg('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordForm(false);
    } catch (err) {
      setPasswordMsg(err.code === 'auth/wrong-password' ? 'Current password is incorrect.' : 'Failed to update password.');
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteMsg('');
    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, deletePassword);
      await reauthenticateWithCredential(user, credential);
      const dbRes = await fetch(`${BASE_URL}/DeleteAccount?email=${encodeURIComponent(user.email)}`, { method: 'DELETE' });
      if (!dbRes.ok) throw new Error('db');
      await deleteUser(user);
      navigate('/login');
    } catch (err) {
      if (err.code === 'auth/wrong-password') {
        setDeleteMsg('Incorrect password.');
      } else if (err.message === 'db') {
        setDeleteMsg('Failed to delete account from database. Please try again.');
      } else {
        setDeleteMsg('Failed to delete account.');
      }
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="home-page">
      <Sidebar initials={initials} />

      <div className="main-content">
        <Navbar title="Settings" subtitle="Manage your account and preferences" />

        <div className="settings-container">
          {/* Profile Card */}
          <div className="settings-card settings-profile-card">
            <div className="settings-profile-header">
              <div className="settings-avatar">{initials}</div>
              <div className="settings-profile-info">
                <h2 className="settings-profile-name">{profile.Full_Name}</h2>
                <p className="settings-profile-email">{profile.Email}</p>
              </div>
            </div>
          </div>

          {/* Account Details */}
          <div className="settings-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 className="settings-card-title" style={{ margin: 0 }}>Account Details</h3>
              {!showEditProfile && (
                <button className="settings-btn settings-btn-outline" onClick={handleEditOpen}>Edit</button>
              )}
            </div>

            {showEditProfile ? (
              <form className="settings-password-form" onSubmit={handleSaveProfile}>
                <label className="settings-detail-label" style={{ marginBottom: 4 }}>Full Name</label>
                <input className="settings-input" value={editForm.Full_Name} onChange={e => setEditForm(f => ({ ...f, Full_Name: e.target.value }))} required />
                <label className="settings-detail-label" style={{ marginBottom: 4, marginTop: 8 }}>Phone Number</label>
                <input className="settings-input" value={editForm.Phone_Number} onChange={e => setEditForm(f => ({ ...f, Phone_Number: formatPhone(e.target.value) }))} />
                <label className="settings-detail-label" style={{ marginBottom: 4, marginTop: 8 }}>Company</label>
                <input className="settings-input" value={editForm.Company} onChange={e => setEditForm(f => ({ ...f, Company: e.target.value }))} />
                <label className="settings-detail-label" style={{ marginBottom: 4, marginTop: 8 }}>Email</label>
                <input className="settings-input" value={profile.Email} disabled style={{ opacity: 0.5 }} />
                {editMsg && <p className="settings-msg error">{editMsg}</p>}
                <div className="settings-delete-actions" style={{ marginTop: 12 }}>
                  <button type="submit" className="settings-btn settings-btn-primary" disabled={editSaving}>{editSaving ? 'Saving…' : 'Save Changes'}</button>
                  <button type="button" className="settings-btn settings-btn-outline" onClick={() => setShowEditProfile(false)}>Cancel</button>
                </div>
              </form>
            ) : (
              <div className="settings-details-grid">
                <div className="settings-detail-item">
                  <div className="settings-detail-icon"><FaUser /></div>
                  <div className="settings-detail-content">
                    <span className="settings-detail-label">Full Name</span>
                    <span className="settings-detail-value">{profile.Full_Name || '—'}</span>
                  </div>
                </div>
                <div className="settings-detail-item">
                  <div className="settings-detail-icon"><FaEnvelope /></div>
                  <div className="settings-detail-content">
                    <span className="settings-detail-label">Email</span>
                    <span className="settings-detail-value">{profile.Email || '—'}</span>
                  </div>
                </div>
                <div className="settings-detail-item">
                  <div className="settings-detail-icon"><FaPhone /></div>
                  <div className="settings-detail-content">
                    <span className="settings-detail-label">Phone Number</span>
                    <span className="settings-detail-value">{formatPhone(profile.Phone_Number) || '—'}</span>
                  </div>
                </div>
                <div className="settings-detail-item">
                  <div className="settings-detail-icon"><FaBuilding /></div>
                  <div className="settings-detail-content">
                    <span className="settings-detail-label">Company</span>
                    <span className="settings-detail-value">{profile.Company || '—'}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Security */}
          <div className="settings-card">
            <h3 className="settings-card-title">Security</h3>
            <div className="settings-security-row">
              <div className="settings-security-info">
                <FaLock className="settings-security-icon" />
                <div>
                  <span className="settings-detail-label">Password</span>
                  <span className="settings-detail-value">••••••••</span>
                </div>
              </div>
              <button
                className="settings-btn settings-btn-outline"
                onClick={() => { setShowPasswordForm(!showPasswordForm); setPasswordMsg(''); }}
              >
                Change Password
              </button>
            </div>

            {showPasswordForm && (
              <form className="settings-password-form" onSubmit={handleChangePassword}>
                <input
                  type="password"
                  placeholder="Current password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="settings-input"
                  required
                />
                <input
                  type="password"
                  placeholder="New password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="settings-input"
                  required
                />
                <input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="settings-input"
                  required
                />
                <button type="submit" className="settings-btn settings-btn-primary">Update Password</button>
                {passwordMsg && <p className={`settings-msg ${passwordMsg.includes('success') ? 'success' : 'error'}`}>{passwordMsg}</p>}
              </form>
            )}
          </div>

          {/* Replay Tutorials */}
          <div className="settings-card">
            <h3 className="settings-card-title">Guided Tours</h3>
            <p className="settings-danger-text" style={{ color: '#6b7280', marginBottom: 14 }}>
              Reset the tutorial for any page so it plays again the next time you visit.
            </p>
            <div className="settings-tours-grid">
              {[
                { label: 'Home',        key: 'hasSeenHomeTutorial' },
                { label: 'Properties',  key: 'hasSeenPropertiesTutorial' },
                { label: 'Activity',    key: 'hasSeenActivityTutorial' },
                { label: 'Work Orders', key: 'hasSeenWorkOrdersTutorial' },
                { label: 'Compare',     key: 'hasSeenCompareTutorial' },
              ].map(({ label, key }) => (
                <button
                  key={key}
                  className="settings-btn settings-btn-outline"
                  onClick={() => {
                    const email = profile?.Email || '';
                    if (email) {
                      localStorage.removeItem(`${key}_${email}`);
                      showToast(`${label} tutorial will play on your next visit to that page.`, 'info');
                    }
                  }}
                >
                  Replay {label} Tutorial
                </button>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div className="settings-card" style={{ textAlign: 'center', padding: '24px 20px' }}>
            <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>
              We would love to hear from you at{' '}
              <a href="mailto:devTeam@dtecx.net" style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}>devTeam@dtecx.net</a>
            </p>
          </div>

          {/* Danger Zone */}
          <div className="settings-card settings-danger-card">
            <h3 className="settings-card-title settings-danger-title">Danger Zone</h3>
            <p className="settings-danger-text">
              Once you delete your account, there is no going back. All your data will be permanently removed.
            </p>
            {!showDeleteConfirm ? (
              <button
                className="settings-btn settings-btn-danger"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <FaTrash /> Delete Account
              </button>
            ) : (
              <div className="settings-delete-confirm">
                <p className="settings-danger-text">Enter your password to confirm:</p>
                <input
                  type="password"
                  placeholder="Your password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  className="settings-input"
                />
                <div className="settings-delete-actions">
                  <button className="settings-btn settings-btn-danger" onClick={handleDeleteAccount}>
                    Confirm Delete
                  </button>
                  <button className="settings-btn settings-btn-outline" onClick={() => { setShowDeleteConfirm(false); setDeleteMsg(''); }}>
                    Cancel
                  </button>
                </div>
                {deleteMsg && <p className="settings-msg error">{deleteMsg}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
